/**
 * Phase 2A · Admin-authenticated voucher-design preview page.
 *
 * Renders any voucher_type by id through the canonical
 * VoucherPresentation gateway. Backed by
 *   GET /api/admin/loyalty/voucher-preview/:vtId
 * which requires a valid admin JWT scoped to the caller's tenant.
 *
 * URL: /admin/voucher-preview/:vtId
 *
 * NOTE: This route is intentionally used ONLY for capturing design
 * screenshots inside the Emergent preview environment. It is NOT a
 * public route and does NOT expose voucher metadata by predictable id.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import VoucherPresentation from '../components/loyalty/VoucherPresentation';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicVoucherPreview() {
  const { vtId } = useParams();
  const [vt, setVt] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    const token = localStorage.getItem('token');
    if (!token) { setError('Admin sign-in required'); return; }
    axios.get(`${API}/api/admin/loyalty/voucher-preview/${vtId}`,
              { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (alive) setVt(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || 'Not found'); });
    return () => { alive = false; };
  }, [vtId]);

  if (error) return <div style={{ padding: 24, color: '#fff', background: '#111', minHeight: '100vh' }}>{error}</div>;
  if (!vt)   return <div style={{ padding: 24, color: '#fff', background: '#111', minHeight: '100vh' }}>Loading…</div>;

  const aspect = Number(vt?.design?.canvas?.aspect_ratio) || 1.586;
  const CARD_W = 560;
  const cardH  = Math.round(CARD_W / aspect);

  return (
    <div data-testid="public-voucher-preview"
         style={{ minHeight: '100vh', background: '#050B12',
                  padding: 48, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', gap: 24 }}>
      <div style={{ color: 'rgba(212,175,55,0.85)',
                    fontFamily: 'Cormorant Garamond, serif',
                    letterSpacing: '0.25em', fontSize: 12 }}>
        VOUCHER PREVIEW · aspect {aspect.toFixed(3)}
      </div>
      <div style={{ position: 'relative', width: CARD_W + 12, height: cardH + 12,
                     borderRadius: 26, padding: 6,
                     background: 'linear-gradient(135deg, rgba(201,162,39,0.30) 0%, rgba(201,162,39,0.06) 40%, rgba(201,162,39,0.30) 100%)',
                     boxShadow: '0 24px 60px -18px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.05)' }}>
        <VoucherPresentation
          voucher={vt}
          design={vt.design}
          outletName={vt.outlet_name || 'Your Brand'}
          state="available"
          validUntil={vt.valid_until || '30 days'}
          size="desktop"
        />
      </div>
      <div style={{ color: 'rgba(255,255,255,0.55)',
                    fontFamily: 'Inter', fontSize: 13, letterSpacing: '0.1em' }}>
        {vt.name} · {vt.code}
      </div>
    </div>
  );
}
